'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { Phone, Mail, MapPin, Clock, Send, CheckCircle } from 'lucide-react';
import { fetchSettings } from '@/lib/supabase/dinauto-service';

interface CompanyInfo {
  phone: string;
  email: string;
  address: string;
  org: string;
  hoursLines: { day: string; hours: string }[];
}

function parseHoursLines(raw: string): { day: string; hours: string }[] {
  if (!raw) return [
    { day: 'Monday – Friday', hours: '08:00 – 18:00' },
    { day: 'Saturday', hours: '09:00 – 15:00' },
    { day: 'Sunday', hours: 'Closed' },
  ];
  return raw.split('|').map((part) => {
    const [day, hours] = part.split(':').map((s) => s.trim());
    return { day: day || '', hours: hours || '' };
  });
}

export default function ContactClient() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '', marketingConsent: false });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [info, setInfo] = useState<CompanyInfo>({
    phone: '+47 4412 673 82',
    email: 'info@din-auto.no',
    address: 'Munkebekken 400\n1061 Oslo, Norway',
    org: '911696282',
    hoursLines: [
      { day: 'Monday – Friday', hours: '08:00 – 18:00' },
      { day: 'Saturday', hours: '09:00 – 15:00' },
      { day: 'Sunday', hours: 'Closed' },
    ],
  });

  useEffect(() => {
    fetchSettings().then((s) => {
      const rawAddr = s['company_address'] || '';
      const addrParts = rawAddr.split(',');
      const addressFormatted = addrParts.length >= 2
        ? `${addrParts[0].trim()}\n${addrParts.slice(1).join(',').trim()}`
        : rawAddr;
      setInfo({
        phone: s['company_phone'] || '+47 4412 673 82',
        email: s['company_email'] || 'info@din-auto.no',
        address: addressFormatted,
        org: s['company_org'] || '911696282',
        hoursLines: parseHoursLines(s['opening_hours'] || ''),
      });
    });
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleCheckbox = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, marketingConsent: e.target.checked }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    await new Promise((r) => setTimeout(r, 900));
    setSubmitted(true);
    setLoading(false);
  };

  const telHref = `tel:${info.phone.replace(/\s/g, '')}`;
  const mailHref = `mailto:${info.email}`;

  return (
    <div className="max-w-screen-xl mx-auto px-6 lg:px-10 py-16">
      {/* Page heading */}
      <div className="mb-14">
        <span className="text-primary text-xs font-bold uppercase tracking-widest">Contact</span>
        <h1 className="font-black text-4xl lg:text-5xl mt-2 leading-tight" style={{ fontWeight: 900 }}>
          Get in touch
        </h1>
        <p className="text-muted-foreground text-lg mt-3 max-w-xl">
          Questions about a reservation? Need help choosing a car? We&apos;re here to help.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        {/* Left: contact info */}
        <div>
          <div className="space-y-8 mb-10">
            {/* Phone */}
            <div className="flex gap-4">
              <div className="w-11 h-11 bg-primary flex items-center justify-center shrink-0">
                <Phone size={18} className="text-foreground" />
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-1">Phone</p>
                <a
                  href={telHref}
                  className="text-xl font-black hover:text-primary transition-colors"
                  style={{ fontWeight: 900 }}
                >
                  {info.phone}
                </a>
                <p className="text-muted-foreground text-sm mt-0.5">Click to call us directly</p>
              </div>
            </div>

            {/* Email */}
            <div className="flex gap-4">
              <div className="w-11 h-11 bg-foreground flex items-center justify-center shrink-0">
                <Mail size={18} className="text-white" />
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-1">Email</p>
                <a
                  href={mailHref}
                  className="text-xl font-black hover:text-primary transition-colors"
                  style={{ fontWeight: 900 }}
                >
                  {info.email}
                </a>
                <p className="text-muted-foreground text-sm mt-0.5">We reply within 24 hours</p>
              </div>
            </div>

            {/* Address */}
            <div className="flex gap-4">
              <div className="w-11 h-11 bg-foreground flex items-center justify-center shrink-0">
                <MapPin size={18} className="text-white" />
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-1">Address</p>
                <p className="text-xl font-black leading-snug whitespace-pre-line" style={{ fontWeight: 900 }}>
                  {info.address}
                </p>
                <p className="text-muted-foreground text-sm mt-0.5">Org.nr. {info.org}</p>
              </div>
            </div>
          </div>

          {/* Opening hours */}
          <div className="bg-muted p-6 mb-8">
            <div className="flex items-center gap-2 mb-4">
              <Clock size={16} className="text-primary" />
              <h3 className="font-black text-sm uppercase tracking-widest" style={{ fontWeight: 900 }}>Opening Hours</h3>
            </div>
            <ul className="space-y-2.5">
              {info.hoursLines.map((h) => (
                <li key={h.day} className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{h.day}</span>
                  <span className={h.hours === 'Closed' ? 'text-muted-foreground/50' : 'font-bold'}>{h.hours}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Map */}
          <div className="w-full h-64 bg-muted overflow-hidden border border-border">
            <iframe
              title="DIN AUTO AS location"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2001.0!2d10.8!3d59.9!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x46416e8a4c0b0001%3A0x1!2sMunkebekken+400%2C+1061+Oslo!5e0!3m2!1sen!2sno!4v1"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>

        {/* Right: contact form */}
        <div>
          <div className="bg-white border border-border p-8">
            <h2 className="font-black text-2xl mb-1" style={{ fontWeight: 900 }}>Send us a message</h2>
            <p className="text-muted-foreground text-sm mb-6">Fill in the form and we&apos;ll get back to you shortly.</p>

            {submitted ? (
              <div className="flex flex-col items-center py-12 text-center">
                <CheckCircle size={48} className="text-green-500 mb-4" />
                <h3 className="font-black text-xl mb-2" style={{ fontWeight: 900 }}>Message sent!</h3>
                <p className="text-muted-foreground text-sm max-w-xs">
                  Thank you for reaching out. We&apos;ll reply to your email within 24 hours.
                </p>
                <button
                  onClick={() => { setSubmitted(false); setForm({ name: '', email: '', phone: '', message: '', marketingConsent: false }); }}
                  className="btn-outline mt-6 text-sm"
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1.5">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    placeholder="Your full name"
                    className="input-field w-full"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1.5">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder="your@email.com"
                    className="input-field w-full"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1.5">
                    Phone
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                    placeholder={info.phone}
                    className="input-field w-full"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1.5">
                    Message <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    placeholder="How can we help you?"
                    rows={5}
                    className="input-field w-full resize-none"
                    required
                  />
                </div>

                {/* Optional marketing consent */}
                <div className="pt-1">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      name="marketingConsent"
                      checked={form.marketingConsent}
                      onChange={handleCheckbox}
                      className="w-4 h-4 mt-0.5 accent-foreground"
                    />
                    <span className="text-sm text-muted-foreground">
                      I agree to receive notifications, offers, and updates from DIN AUTO AS via email and SMS.{' '}
                      <span className="text-xs text-muted-foreground/70">
                        You can unsubscribe at any time. See our{' '}
                        <Link href="/privacy-policy" className="text-foreground underline hover:text-primary transition-colors">
                          Privacy Policy
                        </Link>
                        .
                      </span>
                    </span>
                  </label>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary w-full justify-center gap-2"
                >
                  {loading ? 'Sending…' : (
                    <>
                      <Send size={15} />
                      Send Message
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
